import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import FieldBoxDot from '../../DocumentBoxViewer/FieldBoxDot';

const round2 = (value) => Number((value || 0).toFixed(2));

const toNumber = (raw) => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    return Number.isFinite(n) ? n : 0;
};

/**
 * Gross → net breakdown of a receipt/payment document.
 *
 * The document amount is the gross, so an amount of 200 with 100 withheld at source
 * leaves a net of 100 — and the net is what the payment lines have to add up to.
 * `remainingNet` is what is still unallocated and turns negative once the lines
 * exceed the net.
 *
 * While creating a receipt/payment there is no document amount yet, so the lines are
 * taken as the net and the gross is derived from them instead. Pass `deriveGrossFromLines`
 * for the screens whose amount is the payment lines total rather than the document gross —
 * there the gross follows the lines and the deduction as they are edited.
 */
export const computePaymentAmounts = ({
    amount,
    withholdingTax,
    items = [],
    deriveGrossFromLines = false,
    grossOverride = null,
}) => {
    const documentGross = toNumber(amount);
    const withholding = toNumber(withholdingTax);
    const linesTotal = round2(items.reduce((sum, row) => sum + toNumber(row?.amount), 0));
    const parsedGrossOverride = grossOverride === '' || grossOverride == null ? null : Number(grossOverride);
    const hasGrossOverride = Number.isFinite(parsedGrossOverride);
    const hasDocumentTotal = hasGrossOverride || (!deriveGrossFromLines && documentGross > 0);

    const gross = hasGrossOverride ? round2(parsedGrossOverride) : hasDocumentTotal ? documentGross : round2(linesTotal + withholding);
    const net = round2(gross - withholding);
    const remainingNet = hasDocumentTotal ? round2(net - linesTotal) : 0;
    const isBalanced = Math.abs(remainingNet) <= 0.01;

    return {
        hasDocumentTotal,
        gross,
        withholding,
        net,
        linesTotal,
        remainingNet,
        linesCount: items.length,
        isBalanced,
        hasMismatch: hasDocumentTotal && items.length > 0 && !isBalanced,
        // Pre-fill value for a newly added payment line so one line closes the document.
        suggestedLineAmount: remainingNet > 0.01 ? remainingNet : '',
    };
};

/**
 * Withholding implied by a deduction percent.
 *
 * With a document total the gross is known, so the deduction is that share of the gross.
 * Without one the payment lines are the net, so the gross is derived from them first —
 * the same gross-up the receipt screen uses.
 */
export const computeWithholdingFromPercent = ({ percent, amounts }) => {
    const rate = toNumber(percent);
    if (rate <= 0 || rate >= 100) return 0;

    if (amounts?.hasDocumentTotal) {
        return round2((toNumber(amounts.gross) * rate) / 100);
    }

    const net = toNumber(amounts?.linesTotal);
    if (net <= 0) return 0;
    return round2(net / ((100 - rate) / 100) - net);
};

export const TONES = {
    neutral: { background: '#FFF', border: '1px solid #BFC8FF', label: '#6B7280', value: '#1F2A5B' },
    primary: { background: '#304FFF', border: '1px solid #304FFF', label: 'rgba(255,255,255,0.82)', value: '#FFF' },
    deduction: { background: '#FFF7E8', border: '1px solid #F9AA2A', label: '#9A6100', value: '#9A6100' },
    warning: { background: '#FDECEC', border: '1px solid #D32F2F', label: '#D32F2F', value: '#D32F2F' },
};

export const tileStyle = (colors) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minWidth: '116px',
    padding: '2px 6px',
    borderRadius: '6px',
    background: colors.background,
    border: colors.border,
});

export const labelStyle = (colors) => ({
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: colors.label,
});

export const valueStyle = (colors) => ({
    fontFamily: 'Montserrat',
    fontSize: '15px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: colors.value,
});

/** `ocrField` names the OCR field box (see DocumentBoxViewer) this tile shows. */
export const SummaryTile = ({ label, value, tone = 'neutral', tooltip, ocrField }) => {
    const colors = TONES[tone] || TONES.neutral;
    return (
        <Tooltip title={tooltip || label} placement="top">
            <div style={tileStyle(colors)} data-ocr-field={ocrField}>
                <span style={labelStyle(colors)}>
                    {label}
                    {ocrField && <FieldBoxDot field={ocrField} />}
                </span>
                <span style={valueStyle(colors)}>{`${beautifyNumber(value).trim()} ₪`}</span>
            </div>
        </Tooltip>
    );
};

/**
 * Gross tile the user can type into; the typed value becomes the document amount.
 * While typing, the raw text is kept locally so the field can be cleared.
 */
const EditableGrossTile = ({ label, value, tooltip, onChange }) => {
    const colors = TONES.neutral;
    const [draft, setDraft] = useState(null);

    return (
        <Tooltip title={tooltip || label} placement="top">
            <div
                style={{ ...tileStyle(colors), borderStyle: 'dashed', borderColor: '#304FFF', cursor: 'text' }}
                data-ocr-field="amount"
            >
                <span style={labelStyle(colors)}>
                    {label}
                    <FieldBoxDot field="amount" />
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                        type="number"
                        value={draft ?? (value || '')}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            onChange(e.target.value === '' ? null : Number(e.target.value));
                        }}
                        onBlur={() => setDraft(null)}
                        style={{
                            ...valueStyle(colors),
                            width: '92px',
                            padding: 0,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            textAlign: 'center',
                        }}
                    />
                    <span style={valueStyle(colors)}>₪</span>
                </div>
            </div>
        </Tooltip>
    );
};

const SummaryOperator = ({ children }) => (
    <span style={{ display: 'flex', alignItems: 'center', fontSize: '16px', fontWeight: 700, color: '#304FFF' }}>
        {children}
    </span>
);

/**
 * Reads as an equation in both RTL and LTR: gross − withholding = net, followed by the
 * lines total and, only while the lines do not add up to the net, the remaining balance.
 *
 * Pass `onGrossChange` to let the user type the gross, which is the document amount.
 */
const PaymentAmountsSummaryBar = ({ t, amounts, style, onGrossChange, disabled = false }) => {
    const {
        gross,
        withholding,
        net,
        linesTotal,
        remainingNet,
        linesCount,
        isBalanced,
        hasMismatch,
        hasDocumentTotal,
    } = amounts;

    const isGrossEditable = typeof onGrossChange === 'function' && !disabled;

    if (!gross && !withholding && linesCount === 0 && !isGrossEditable) return null;

    const remainingTone = remainingNet < 0 ? 'warning' : 'deduction';

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '6px',
                padding: '6px 10px',
                marginBottom: '8px',
                borderRadius: '8px',
                background: 'rgba(234, 237, 255, 0.55)',
                border: '1px solid #BFC8FF',
                ...style,
            }}
        >
            {isGrossEditable ? (
                <EditableGrossTile
                    label={t('TotalGross')}
                    value={gross}
                    tooltip={t('TotalGrossEditableTooltip')}
                    onChange={onGrossChange}
                />
            ) : (
                <SummaryTile label={t('TotalGross')} value={gross} ocrField="amount" />
            )}
            <SummaryOperator>−</SummaryOperator>
            <SummaryTile label={t('WithholdingAtSource')} value={withholding} tone="deduction" ocrField="withholdingTax" />
            <SummaryOperator>=</SummaryOperator>
            <SummaryTile label={t('TotalNet')} value={net} tone="primary" />

            {hasDocumentTotal && (
                <>
                    <div style={{ width: '1px', alignSelf: 'stretch', background: '#BFC8FF', margin: '0 4px' }} />

                    <SummaryTile label={t('LinesTotal')} value={linesTotal} tooltip={t('PaymentTotal')} />
                    {/* Nothing left to allocate is the expected state, so the balance is only shown when it matters. */}
                    {!isBalanced && (
                        <SummaryTile
                            label={t('RemainingNet')}
                            value={remainingNet}
                            tone={remainingTone}
                            tooltip={t('RemainingNetTooltip')}
                        />
                    )}
                </>
            )}

            {hasMismatch && (
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#D32F2F' }}>
                    {t('AmountDoesNotMatchDocument')}
                </span>
            )}
        </div>
    );
};

export default PaymentAmountsSummaryBar;
